import React, { useState } from "react";
import {
  TrendingUp,
  Clock,
  Target,
  Award,
  AlertCircle,
  BookOpen,
} from "lucide-react";
import ExamAttemptDialog from "./ExamAttemptDialog";

const getSessionProgress = (sessionId) => {
  try {
    const [, startStr, endStr] = sessionId.match(/^S(\d{6})_(\d{6})$/) || [];

    if (!startStr || !endStr) return 0;

    const parseDate = (value) => {
      const day = parseInt(value.slice(0, 2), 10);
      const month = parseInt(value.slice(2, 4), 10) - 1;
      const year = 2000 + parseInt(value.slice(4, 6), 10);

      return new Date(year, month, day);
    };

    const startDate = parseDate(startStr);
    const endDate = parseDate(endStr);
    const today = new Date();

    const totalDuration = endDate - startDate;
    const elapsedDuration = today - startDate;

    if (today <= startDate) return 0;
    if (today >= endDate) return 100;

    return (elapsedDuration / totalDuration) * 100;
  } catch {
    return 0;
  }
};

const OverviewSection = ({
  overviewData,
  sessionId,
  syllabusId,
  syllabusProgress,
  userId,
}) => {
  const [isResponseDialogOpen, setResponseDialogOpen] = useState(false);
  const colorClasses = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };

  const statusColor = overviewData.studentStatus.color;

  const progress = Math.min((overviewData.questionsSolved / 20) * 100, 100);

  // Timeline progress
  const expectedProgress = getSessionProgress(sessionId);
  const isBehind = syllabusProgress < expectedProgress - 5;

  const stats = [
    {
      icon: Award,
      value: `${overviewData.avgPercentage}%`,
      label: "Score",
      color: "text-blue-600",
    },
    {
      icon: Target,
      value: `${overviewData.avgAccuracy}%`,
      label: "Accuracy",
      color: "text-emerald-600",
    },
    {
      icon: Clock,
      value: `${overviewData.avgTimePerQuestion}s`,
      label: "Time/Q",
      color: "text-purple-600",
    },
    {
      icon: TrendingUp,
      value: overviewData.totalAttempts,
      label: "Attempts",
      color: "text-orange-600",
    },
    {
      icon: BookOpen,
      value: `${syllabusProgress?.toFixed(2) || 0}%`,
      label: "Syllabus",
      color: isBehind ? "text-red-600" : "text-indigo-600",
      isBehind,
    },
  ];

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 p-4 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-900">Overview</h2>

          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium ${colorClasses[statusColor]}`}
          >
            <AlertCircle className="w-3.5 h-3.5" />
            {overviewData.studentStatus.name}
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="border border-gray-100 rounded-lg p-3"
              onClick={() => {
                if (stat.label == "Attempts") {
                  setResponseDialogOpen(true);
                }
              }}
            >
              <div className="flex items-center justify-between">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />

                {stat.isBehind && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-medium">
                    Behind
                  </span>
                )}
              </div>

              <div className="mt-2 text-lg font-bold text-gray-900">
                {stat.value}
              </div>

              <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Questions Solved Progress */}
        <div className="mb-3">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Questions Solved</span>
            <span className="font-semibold text-gray-900">
              {overviewData.questionsSolved}/20
            </span>
          </div>

          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Status Message */}
        <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-800 border border-amber-100">
          {overviewData.studentStatus.shortMessage}
        </div>
      </div>

      <ExamAttemptDialog
        userId={userId}
        syllabusId={syllabusId}
        sessionId={sessionId}
        isOpen={isResponseDialogOpen}
        onClose={() => setResponseDialogOpen(false)}
      />
    </>
  );
};

export default OverviewSection;
